(() => {
  const ROOT_SELECTOR = '[data-jm-global-header]';
  const INITIALIZED = 'jmGlobalHeaderInitialized';

  const pushEvent = (eventName, detail = {}) => {
    if (!Array.isArray(window.dataLayer)) return;
    window.dataLayer.push({
      event: eventName,
      jm_location: 'global_header',
      ...detail
    });
  };

  const setStatus = (root, message, state = 'info', targetId = '') => {
    const statuses = Array.from(root.querySelectorAll('[data-jm-header-voice-status]'));
    const status = targetId
      ? statuses.find((item) => item.id === targetId)
      : statuses[0];

    if (!status) return;
    status.hidden = false;
    status.dataset.state = state;
    status.textContent = message;
  };

  const initializeVoiceButton = (root, button) => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const targetId = button.dataset.jmHeaderVoiceTarget;
    const input = targetId ? root.querySelector(`#${CSS.escape(targetId)}`) : null;
    const statusId = button.getAttribute('aria-controls') || '';
    const label = button.querySelector('[data-jm-header-voice-label]');

    if (!Recognition || !input) return;

    button.hidden = false;

    let recognition = null;
    let isListening = false;

    const reset = () => {
      isListening = false;
      button.setAttribute('aria-pressed', 'false');
      if (label) label.textContent = 'Speak your RC model';
    };

    button.addEventListener('click', () => {
      if (isListening && recognition) {
        recognition.stop();
        return;
      }

      recognition = new Recognition();
      recognition.lang = document.documentElement.lang || 'en-US';
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.addEventListener('start', () => {
        isListening = true;
        button.setAttribute('aria-pressed', 'true');
        if (label) label.textContent = 'Stop listening';
        setStatus(root, 'Listening… say your RC brand and model.', 'info', statusId);
        pushEvent('jm_voice_search_start');
      });

      recognition.addEventListener('result', (event) => {
        const transcript = event.results?.[0]?.[0]?.transcript?.trim();
        if (!transcript) {
          setStatus(root, 'We did not catch that. Try again or type your RC model.', 'error', statusId);
          return;
        }

        input.value = transcript;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
        setStatus(root, 'Review the recognized model before searching.', 'info', statusId);
        pushEvent('jm_voice_search_transcript_ready');
      });

      recognition.addEventListener('error', (event) => {
        const denied = event.error === 'not-allowed' || event.error === 'service-not-allowed';
        setStatus(
          root,
          denied
            ? 'Microphone unavailable. Type your model or use your device keyboard’s dictation.'
            : 'We did not catch that. Try again or type your RC model.',
          'error',
          statusId
        );
        pushEvent('jm_voice_search_error', {
          jm_voice_error: denied ? 'permission_denied' : 'recognition_error'
        });
      });

      recognition.addEventListener('end', reset);

      try {
        recognition.start();
      } catch (error) {
        reset();
        setStatus(
          root,
          'Microphone unavailable. Type your model or use your device keyboard’s dictation.',
          'error',
          statusId
        );
      }
    });
  };

  const initializeSearchReadiness = (form) => {
    const input = form.querySelector('[data-jm-header-search-input]');
    if (!input || typeof form.requestSubmit !== 'function') return;

    let pending = false;
    let fallback = false;
    let timer = null;
    const ready = () => input.classList.contains('snize-input-style');

    const finish = (allowFallback = false) => {
      if (!pending || (!allowFallback && !ready())) return;
      pending = false;
      window.clearTimeout(timer);
      document.removeEventListener('Searchanise.Loaded', onLoaded);
      form.removeAttribute('aria-busy');
      if (!form.isConnected || !input.isConnected || !input.value.trim()) return;
      fallback = allowFallback && !ready();
      try {
        form.requestSubmit();
      } finally {
        fallback = false;
      }
    };

    const onLoaded = () => finish();

    // Searchanise installs its native redirects asynchronously.
    const deferUntilReady = (event) => {
      if (fallback || ready() || !input.value.trim()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (pending) return;
      pending = true;
      form.setAttribute('aria-busy', 'true');
      document.addEventListener('Searchanise.Loaded', onLoaded);
      timer = window.setTimeout(() => finish(true), 3000);
    };

    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.isComposing) return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      deferUntilReady(event);
    }, true);
    form.addEventListener('submit', deferUntilReady, true);
  };

  const initializeRoot = (root) => {
    if (!root || root.dataset[INITIALIZED] === 'true') return;
    root.dataset[INITIALIZED] = 'true';

    const openButton = root.querySelector('[data-jm-menu-open]');
    const closeButton = root.querySelector('[data-jm-menu-close]');
    const dialog = root.querySelector('[data-jm-menu-dialog]');
    const searchToggle = root.querySelector('[data-jm-search-toggle]');
    const mobileSearch = root.querySelector('[data-jm-mobile-search]');
    let returnFocus = null;

    const closeDialog = () => {
      if (!dialog) return;
      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
      openButton?.setAttribute('aria-expanded', 'false');
      returnFocus?.focus();
    };

    openButton?.addEventListener('click', () => {
      if (!dialog) return;
      returnFocus = openButton;
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      openButton.setAttribute('aria-expanded', 'true');
      closeButton?.focus();
    });

    closeButton?.addEventListener('click', closeDialog);

    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });

    dialog?.addEventListener('close', () => {
      openButton?.setAttribute('aria-expanded', 'false');
    });

    dialog?.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => {
        if (dialog.open) closeDialog();
      });
    });

    searchToggle?.addEventListener('click', () => {
      if (!mobileSearch) return;
      const willOpen = mobileSearch.hidden;
      mobileSearch.hidden = !willOpen;
      searchToggle.setAttribute('aria-expanded', String(willOpen));
      searchToggle.setAttribute('aria-label', willOpen ? 'Close model search' : 'Open model search');
      if (willOpen) {
        mobileSearch.querySelector('[data-jm-header-search-input]')?.focus();
      }
    });

    root.querySelectorAll('[data-jm-header-search-form]').forEach((form) => {
      initializeSearchReadiness(form);
      let submitCount = 0;

      form.addEventListener('submit', () => {
        submitCount += 1;
        pushEvent('jm_search_submit', {
          jm_search_method: 'typed_or_confirmed_voice'
        });
      });

      if (!form.classList.contains('jm-global-header__search--mobile')) return;

      const input = form.querySelector('[data-jm-header-search-input]');
      input?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.repeat || event.isComposing) return;
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (!input.value.trim()) return;

        const observedSubmitCount = submitCount;
        window.setTimeout(() => {
          if (submitCount !== observedSubmitCount) return;
          if (!form.isConnected || !input.isConnected) return;

          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
          }
        }, 0);
      });
    });

    root.querySelectorAll('[data-jm-header-voice-button]').forEach((button) => {
      initializeVoiceButton(root, button);
    });
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initializeRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initializeRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });
})();
